import { Link } from 'react-router';
import { MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { useLocations } from '@/hooks/useLocations';
import { Button } from '@/components/ui/button';

export const LocationsPage = () => {
    const { data: locations, isLoading, error, refetch, isFetching } = useLocations();

    if (isLoading) {
        return (
            <div
                className="flex justify-center items-center min-h-[50vh]"
                role="status"
                aria-label="Loading locations"
                aria-live="polite"
            >
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[50vh] bg-white px-6">
                <div className="w-full max-w-xl rounded-3xl border border-destructive/15 bg-destructive/5 p-8 text-center shadow-sm">
                    <h2 className="text-2xl font-semibold text-foreground">We couldn&apos;t load our locations</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                        Please try again in a moment. If you need help planning a pickup, our team is happy to help.
                    </p>
                    <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Button onClick={() => void refetch()} disabled={isFetching} className="w-full sm:w-auto">
                            Try again
                        </Button>
                        <Button asChild variant="outline" className="w-full sm:w-auto">
                            <Link to="/contact">Contact our team</Link>
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (!locations?.length) {
        return (
            <>
                <div className="bg-white py-24 sm:py-32">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl text-center">
                            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Our Locations</h2>
                            <p className="mt-2 text-lg leading-8 text-muted-foreground">
                                We&apos;re getting our pickup locations ready. If you already know where you&apos;ll be staying, our team can help you plan the smoothest handoff.
                            </p>
                            <div className="mt-8 flex justify-center">
                                <Button asChild>
                                    <Link to="/contact">Contact our team</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="bg-white py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Our Locations</h2>
                        <p className="mt-2 text-lg leading-8 text-muted-foreground">
                            We serve major airports and cities to make your travel as convenient as possible.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 lg:mx-0 lg:max-w-none lg:grid-cols-2">
                        {locations?.map((location) => {
                            const imageUrl = location.imageUrl?.trim();
                            const pickupInstructions = location.specialInstructions?.trim();

                            return (
                            <article key={location.id} className="flex flex-col items-start justify-between overflow-hidden rounded-2xl border bg-muted transition-shadow hover:shadow-md">
                                {imageUrl && (
                                    <div className="aspect-[4/3] w-full overflow-hidden bg-muted/60">
                                        <img
                                            src={imageUrl}
                                            alt={location.name}
                                            className="h-full w-full object-cover"
                                        />
                                    </div>
                                )}

                                <div className="w-full p-8">
                                    <div className="mb-4 flex items-center gap-x-4 text-xs">
                                        <div className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
                                            <MapPin className="h-3 w-3" />
                                            {location.city}, {location.country}
                                        </div>
                                        {location.isPickupAvailable && (
                                            <span className="font-medium text-muted-foreground">Pickup Available</span>
                                        )}
                                    </div>

                                    <h3 className="mb-4 text-xl font-bold leading-6 text-foreground">
                                        <Link to={`/locations/${location.slug || location.id}`} className="hover:text-primary">
                                            {location.name}
                                        </Link>
                                    </h3>

                                    <p className="mb-6 line-clamp-3 whitespace-pre-line text-sm leading-6 text-muted-foreground">
                                        {location.description}
                                    </p>

                                    {pickupInstructions && (
                                        <div className="mb-6 rounded-2xl border border-warning-border bg-warning-subtle/60 p-4">
                                            <h4 className="text-sm font-semibold text-foreground">Pickup Instructions</h4>
                                            <p className="mt-2 line-clamp-2 whitespace-pre-line text-sm leading-6 text-muted-foreground">
                                                {pickupInstructions}
                                            </p>
                                        </div>
                                    )}

                                    <div className="w-full space-y-3 border-t border-border pt-6 text-sm text-muted-foreground">
                                        {location.operatingHours && (
                                            <div className="flex items-start gap-3">
                                                <Clock className="h-5 w-5 shrink-0 text-primary" />
                                                <span>{location.operatingHours}</span>
                                            </div>
                                        )}

                                        {location.phone && (
                                            <div className="flex items-center gap-3">
                                                <Phone className="h-5 w-5 shrink-0 text-primary" />
                                                <a href={`tel:${location.phone}`} className="hover:text-primary">{location.phone}</a>
                                            </div>
                                        )}

                                        {location.email && (
                                            <div className="flex items-center gap-3">
                                                <Mail className="h-5 w-5 shrink-0 text-primary" />
                                                <a href={`mailto:${location.email}`} className="hover:text-primary">{location.email}</a>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 w-full">
                                        <Button asChild variant="outline" className="w-full gap-2">
                                            <Link to={`/locations/${location.slug || location.id}`}>
                                                View Details <ArrowRight className="h-4 w-4" />
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                            </article>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};
